import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import CapsuleTab from '@repo/ui/capsule-tab'
import { Column } from '@repo/ui/data-table'
import {
  FamilyThreeIcon,
  HealthFormIcon,
  ListApprovedIcon,
  PrintIcon,
  UploadIcon,
  UserTwoIcon
} from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getAffectedStaffActions } from '../../../api/affected-staffs/get-affected-staff-actions'
import { API, constructApiUrl } from '../../../api/apis.config'
import DynamicDataTable, {
  DynamicDataTableProps
} from '../../../components/elements/dynamic-table'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import HostFamilyInformationData from '../../../components/pages/ols/host-family/host-family-information/host-family-information-data'
import { EnrollmentsAffectedStaffGridView } from '../../../components/pages/staff/staff-personal-space/enrollment-card'
import { affectedStaffEnrollmentsColumns } from '../../../components/pages/staff/staff-personal-space/utils'
import useAffectedStaff from '../../../lib/hooks/use-affected-staff'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useEnrollmentsPickupListCount } from '../../../lib/hooks/use-enrollments-pickup-list-count'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { AccomodationEnum } from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { StaffQualificationEnum } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { EnrollmentCompact } from '../../../lib/types/models/enrollment/enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentFilter } from '../../../lib/types/models/enrollment/enrollment-filter'
import { HostFamilyLight } from '../../../lib/types/models/host-family/host-family'

type ViewType = 'list' | 'grid'

const viewModeMapping: Record<ViewType, number> = {
  list: 0,
  grid: 1
}

export default function StaffPersonalSpaceJourneyDetailsStudents() {
  const { t } = useTranslate()
  const { downloadDocuments } = useDownloadDocuments()
  const { affectedStaff, isLoading } = useAffectedStaff()
  const [searchParams, setSearchParams] = useSearchParams()
  const viewMode = searchParams.get('viewMode') as ViewType
  const { toggleModal, modalState, selectedData } = useModalManager<
    'host-family',
    HostFamilyLight
  >(['host-family'])

  const { data: affectedStaffActions } = useQuery({
    queryKey: ['affected-staff-actions', affectedStaff?.id],
    queryFn: () => {
      if (affectedStaff?.id) return getAffectedStaffActions(affectedStaff.id)
    },
    enabled: !!affectedStaff?.id
  })

  const actions: DynamicDataTableProps<EnrollmentCompact, null, undefined>['actions'] = [
    {
      label: t('staff.personal.space.students.table.actions.add.request'),
      variant: 'primary',
      icon: HealthFormIcon
    },
    {
      label: t('staff.personal.space.students.table.actions.print.youth.list'),
      variant: 'ghost',
      icon: PrintIcon
    },
    {
      label: t('staff.personal.space.students.table.actions.print.youth.list.by.level'),
      variant: 'ghost',
      icon: PrintIcon
    },
    {
      label: t('staff.personal.space.students.table.actions.export.youth.list.to.excel'),
      variant: 'ghost',
      icon: UploadIcon
    },
    {
      label: t('staff.personal.space.students.table.actions.print.youth.list.by.host.family'),
      variant: 'ghost',
      icon: PrintIcon
    }
  ]

  const columns: Column<EnrollmentCompact>[] = [
    ...affectedStaffEnrollmentsColumns(t),
    {
      accessorKey: 'id',
      header: t('staff.personal.space.students.columns.host.family.name'),
      cell: ({ row }) => {
        const hostFamily = EnrollmentProperties.getHostFamily(row.original)
        if (!hostFamily) return '-'
        return (
          <Typography.Link
            variant={'secondary-body'}
            color={tailwindColors.vividBlue.DEFAULT}
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('host-family', hostFamily)
            }}
          >
            {StringFormatter.capitalize(hostFamily.familyName)}
          </Typography.Link>
        )
      }
    }
  ]

  useEffect(() => {
    // handle invalid view mode or no view mode
    if (!viewMode || !Object.keys(viewModeMapping).includes(viewMode))
      setSearchParams({ viewMode: 'list' })
  }, [searchParams])

  const centerIds = affectedStaff?.staffRequest.centers?.map((c) => c.id)

  // Only the director of a France stay prints the "who collects the youth" list (youth without
  // LEC transport); other staff and foreign stays never see the CTA
  const canPrintPickupList =
    affectedStaff?.staffRequest.country?.code === 'FRA' &&
    affectedStaff?.staffRequest.qualification === StaffQualificationEnum.DIRECTOR
  const { hasCandidates: hasPickupListCandidates } = useEnrollmentsPickupListCount(
    centerIds?.at(0),
    canPrintPickupList
  )

  if (isLoading) return <LoadingScreen />

  if (!affectedStaff || !centerIds || centerIds.length === 0)
    return (
      <div className={'flex justify-center items-center h-full'}>
        <Typography variant={'h4'}>
          {t('staff.personal.space.my.journeys.details.students.invalid.id.or.center.data')}
        </Typography>
      </div>
    )

  if (!affectedStaffActions?.canViewCenterEnrollments) {
    return (
      <div className={'p-2'}>
        <Typography variant={'body-medium'} color={tailwindColors.grey.DEFAULT}>
          {t('staff.personal.space.my.journeys.details.students.no.enrollments.permission')}
        </Typography>
      </div>
    )
  }

  return (
    <div className={'flex flex-col gap-4'}>
      <Typography variant="h3">{t('staff.personal.space.students.title')}</Typography>

      <CapsuleTab
        outline
        variant={'secondary'}
        iconPosition={'left'}
        activeTab={viewModeMapping[viewMode]}
        className={'w-80 rounded-full justify-center '}
        onTabChange={(index) => {
          const newViewMode = Object.keys(viewModeMapping)[index] as ViewType
          setSearchParams({ viewMode: newViewMode })
        }}
        tabs={[
          { label: t('staff.personal.space.students.view.list'), icon: ListApprovedIcon },
          { label: t('staff.personal.space.students.view.grid'), icon: UserTwoIcon }
        ]}
      />

      {viewMode === 'list' ? (
        <Box className={'border border-grey-light rounded-xl p-4'}>
          <div className="flex flex-wrap gap-3">
            {affectedStaff?.staffRequest.centers.every(
              (center) => center.accomodation !== AccomodationEnum.HOST_FAMILY
            ) && (
              <Button
                icon={HealthFormIcon}
                label="Voir les fiches sanitaires de liaisons"
                onClick={async () =>
                  await Promise.all(
                    centerIds.map(async (id) => {
                      await downloadDocuments({
                        endpoint: constructApiUrl(API.centers.exportEnrollmentsHealth.url, {
                          id
                        })
                      })
                    })
                  )
                }
              />
            )}
            <Button
              icon={PrintIcon}
              variant="ghost"
              label="Imprimer la liste des jeunes"
              onClick={async () =>
                await Promise.all(
                  centerIds.map(async (id) => {
                    await downloadDocuments({
                      endpoint: constructApiUrl(API.centers.printFullListOfEnrollments.url, {
                        id
                      })
                    })
                  })
                )
              }
            />
            <Button
              icon={PrintIcon}
              variant="ghost"
              label="Imprimer la liste des jeunes par niveau"
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(
                    API.affectedStaffs.exportEnrollmentsListByTestGrades.url,
                    { id: affectedStaff.id }
                  )
                })
              }
            />
            <Button
              icon={UploadIcon}
              variant="ghost"
              label="Exporter la liste des jeunes en Excel"
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(API.affectedStaffs.exportEnrollmentsListExcel.url, {
                    id: affectedStaff.id
                  })
                })
              }
            />
            <Button
              icon={PrintIcon}
              variant="ghost"
              label="Imprimer la liste des jeunes par famille-hôtesse"
              onClick={async () =>
                await Promise.all(
                  centerIds.map(async (id) => {
                    await downloadDocuments({
                      endpoint: constructApiUrl(
                        API.centers.printPdfListOfEnrollmentsPlacementByHostFamily.url,
                        { id }
                      )
                    })
                  })
                )
              }
            />
            <Button
              icon={PrintIcon}
              variant="ghost"
              label="Imprimer la liste des jeunes par mode de transport"
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(
                    API.affectedStaffs.printEnrollmentsListByMeansOfTransport.url,
                    { id: affectedStaff.id }
                  )
                })
              }
            />
            <Button
              icon={PrintIcon}
              variant="ghost"
              label="Imprimer les Bulletins d'Inscriptions"
              onClick={() =>
                downloadDocuments({
                  endpoint: constructApiUrl(
                    API.affectedStaffs.printEnrollmentsRegistrationForms.url,
                    { id: affectedStaff.id }
                  )
                })
              }
            />
            {canPrintPickupList && (
              <Button
                icon={PrintIcon}
                variant="ghost"
                disabled={!hasPickupListCandidates}
                label="Liste personne désignée au retour - voyages indiv"
                onClick={() =>
                  downloadDocuments({
                    endpoint: constructApiUrl(API.centers.printEnrollmentsPickupList.url, {
                      id: centerIds[0]
                    })
                  })
                }
              />
            )}
          </div>

          <DynamicDataTable<EnrollmentCompact, EnrollmentFilter, undefined, 'COMPACT'>
            headerType={'minimal'}
            version={'COMPACT'}
            columns={columns}
            className="p-0"
            endpoint={constructApiUrl(API.affectedStaffs.getAffectedStaffEnrollments.url, {
              id: affectedStaff.id
            })}
            permissions={{ delete: false }}
            actions={actions}
            displayOrder
          />
        </Box>
      ) : (
        <EnrollmentsAffectedStaffGridView affectedStaffId={affectedStaff.id} />
      )}

      {selectedData && (
        <Modal
          hideActions
          size={'large'}
          icon={FamilyThreeIcon}
          isOpen={modalState['host-family']}
          onCancel={() => toggleModal('host-family')}
          title={t('staff.personal.space.students.columns.host.family.modal.title')}
        >
          <div className={MODAL_CONTENT_CLASSNAME}>
            <HostFamilyInformationData id={selectedData.id} view={'simple'} />
          </div>
        </Modal>
      )}
    </div>
  )
}
